import React, { useState, useEffect } from 'react';
import { CustomerForm } from './CustomerForm';
import { AddressForm } from './AddressForm';
import { PhoneVerification } from './PhoneVerification';
import { CustomerData, AddressData } from './types';
import { useNomenclator } from '../../hooks';

interface OrderFlowProps {
  onComplete: (data: { customer: CustomerData; address: AddressData }) => void;
  orderId?: number;
  paymentMethod?: 'STRIPE' | 'RAMBURS';
  onVerified?: () => void;
}

type OrderStep = 'customer' | 'address' | 'verify-phone';

export const OrderFlow: React.FC<OrderFlowProps> = ({ 
  onComplete, 
  orderId, 
  paymentMethod,
  onVerified 
}) => {
  const [step, setStep] = useState<OrderStep>('customer');
  const [customerData, setCustomerData] = useState<CustomerData | null>(null);
  const { preloadNomenclator, isLoading, error } = useNomenclator();

  // Start preloading nomenclator data immediately
  useEffect(() => {
    if (step === 'customer') {
      // Preload in background during customer data entry
      preloadNomenclator().catch(console.error);
    }
  }, [step, preloadNomenclator]);

  const handleCustomerSubmit = (data: CustomerData) => {
    setCustomerData(data);
    setStep('address');
  };

  const handleAddressSubmit = (data: AddressData) => {
    // If RAMBURS payment and we have an orderId, proceed to phone verification
    if (paymentMethod === 'RAMBURS' && orderId) {
      setStep('verify-phone');
    } else if (customerData) {
      // Otherwise complete the flow
      onComplete({
        customer: customerData,
        address: data
      });
    }
  };

  const handleVerificationComplete = () => {
    if (onVerified) {
      onVerified();
    }
  };

  const handleBack = () => {
    if (step === 'address') {
      setStep('customer');
    } else if (step === 'verify-phone') {
      setStep('address');
    }
  };

  // Render loading state if nomenclator is still loading during address step
  if (step === 'address' && isLoading) {
    return (
      <div className="flex flex-col items-center justify-center py-6">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-500 mx-auto"></div>
          <p className="mt-2 text-sm text-gray-400">Se încarcă datele necesare...</p>
        </div>
      </div>
    );
  }

  // Render error state if nomenclator fails to load during address step
  if (step === 'address' && error) {
    return (
      <div className="flex flex-col items-center justify-center py-6">
        <div className="text-center">
          <p className="text-red-500 text-sm mb-4">{error}</p>
          <div className="space-x-4">
            <button
              onClick={handleBack}
              className="text-sm text-gray-400 hover:text-white px-3 py-1 rounded-md border border-gray-700 hover:border-gray-600"
              aria-label="Înapoi la datele personale"
            >
              Înapoi
            </button>
            <button
              onClick={() => preloadNomenclator()}
              className="text-sm text-gray-400 hover:text-white px-3 py-1 rounded-md border border-gray-700 hover:border-gray-600"
              aria-label="Reîncearcă încărcarea datelor"
            >
              Reîncearcă
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      {step === 'customer' && (
        <div>
          <h2 className="text-base font-medium mb-3 text-gray-200">Date personale</h2>
          <CustomerForm onSubmit={handleCustomerSubmit} />
        </div>
      )}

      {step === 'address' && (
        <div>
          <h2 className="text-base font-medium mb-3 text-gray-200">
            <button
              onClick={handleBack}
              className="mr-2 text-gray-400 hover:text-white focus:outline-none"
              aria-label="Înapoi la datele personale"
              title="Înapoi la datele personale"
            >
              <svg 
                className="w-5 h-5 inline-block -mt-0.5" 
                fill="none" 
                viewBox="0 0 24 24" 
                stroke="currentColor"
                aria-hidden="true"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              <span className="sr-only">Înapoi la datele personale</span>
            </button>
            Adresa de livrare
          </h2>
          <AddressForm onSubmit={handleAddressSubmit} />
        </div>
      )}

      {step === 'verify-phone' && customerData && orderId && (
        <div>
          <h2 className="text-base font-medium mb-3 text-gray-200">
            <button
              onClick={handleBack}
              className="mr-2 text-gray-400 hover:text-white focus:outline-none"
              aria-label="Înapoi la adresa de livrare"
              title="Înapoi la adresa de livrare"
            >
              <svg 
                className="w-5 h-5 inline-block -mt-0.5" 
                fill="none" 
                viewBox="0 0 24 24" 
                stroke="currentColor"
                aria-hidden="true"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              <span className="sr-only">Înapoi la adresa de livrare</span>
            </button>
            Comandă în curs de confirmare!
          </h2>
          <PhoneVerification
            orderId={orderId}
            phoneNumber={customerData.phone}
            onVerified={handleVerificationComplete}
            onCancel={handleBack}
          />
        </div>
      )}

      {/* Progress indicator */}
      <div className="mt-3 flex justify-center">
        <div 
          className="flex items-center space-x-1.5"
          role="status"
          aria-label={`Pasul ${
            step === 'customer' ? '1' : 
            step === 'address' ? '2' : '3'
          } din ${paymentMethod === 'RAMBURS' ? '3' : '2'}: ${
            step === 'customer' ? 'Date personale' : 
            step === 'address' ? 'Adresa de livrare' : 
            'Confirmare comandă'
          }`}
        >
          <div 
            className={`h-1.5 w-1.5 rounded-full transition-colors duration-200
              ${step === 'customer' ? 'bg-red-500' : 'bg-gray-700'}`}
            aria-hidden="true"
          />
          <div 
            className={`h-1.5 w-1.5 rounded-full transition-colors duration-200
              ${step === 'address' ? 'bg-red-500' : 'bg-gray-700'}`}
            aria-hidden="true"
          />
          {paymentMethod === 'RAMBURS' && (
            <div 
              className={`h-1.5 w-1.5 rounded-full transition-colors duration-200
                ${step === 'verify-phone' ? 'bg-red-500' : 'bg-gray-700'}`}
              aria-hidden="true"
            />
          )}
        </div>
      </div>
    </div>
  );
};
